import React from 'react';
import { Box } from '@mui/material';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

// Register the required Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * TestSuiteDistributionChart component for displaying test cases distribution by test suite
 * @param {Object} props - Component props
 * @param {Array} props.suiteData - Array of test suite data objects
 * @returns {JSX.Element} TestSuiteDistributionChart component
 */
const TestSuiteDistributionChart = ({ suiteData = [] }) => {
  // Generate colors for each test suite
  const generateColors = (count) => {
    const colors = [
      '#2196f3', // Blue
      '#4caf50', // Green
      '#ff9800', // Orange
      '#f44336', // Red
      '#9c27b0', // Purple
      '#00bcd4', // Cyan
      '#ffeb3b', // Yellow
      '#795548', // Brown
      '#607d8b', // Blue Grey
      '#e91e63', // Pink
      '#3f51b5', // Indigo
      '#8bc34a', // Light Green
      '#ffc107', // Amber
      '#673ab7', // Deep Purple
      '#009688', // Teal
    ];
    
    // If we need more colors than predefined, generate them
    const result = [];
    for (let i = 0; i < count; i++) {
      if (i < colors.length) {
        result.push(colors[i]);
      } else {
        // Generate a random color
        const hue = (i * 137.508) % 360; // Golden angle approximation
        result.push(`hsl(${hue}, 70%, 50%)`);
      }
    }
    return result;
  };

  // Extract test suite names and counts
  const testSuites = suiteData.map(item => item.testSuite);
  const counts = suiteData.map(item => item.count);
  const colors = generateColors(testSuites.length);

  // Chart data
  const chartData = {
    labels: testSuites,
    datasets: [
      {
        data: counts,
        backgroundColor: colors,
        borderColor: colors.map(() => '#ffffff'),
        borderWidth: 2,
        hoverOffset: 8,
        hoverBorderWidth: 3,
      }
    ]
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Test Cases Distribution by Test Suite',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      legend: {
        position: 'right',
        labels: {
          boxWidth: 15,
          padding: 10,
          usePointStyle: true,
          font: {
            size: 11
          },
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i];
                const percentage = Math.round((value / total) * 100);
                return {
                  text: `${label} (${percentage}%)`,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  strokeStyle: data.datasets[0].borderColor[i],
                  lineWidth: data.datasets[0].borderWidth,
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#ffffff',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} tests (${percentage}%)`;
          }
        }
      }
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10
      }
    }
  };

  // If no data, display a message
  if (suiteData.length === 0) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        height="100%"
        minHeight="300px"
      >
        No test suite data available
      </Box>
    );
  }

  return (
    <Box height="400px" width="100%" overflow="hidden">
      <Pie data={chartData} options={options} />
    </Box>
  );
};

export default TestSuiteDistributionChart;
